import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDialog,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useCreatePurchaseBrokerageCancelationDocumentMutation,
  useEndPurchaseBrokerageBusinessCaseMutation,
  useGetBusinessCaseQuery,
} from '@dms/api/sales';
import {EntityResourceIds, FileResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {
  DocumentsDialog,
  DocumentTemplateBox,
  handleApiError,
  TemplateBoxTemplate,
  useCodeList,
  useDocumentTemplates,
  usePermissions,
} from '@dms/shared';

import {composePath, suffixTestId, TestIdProps, useNavigate, yupString} from 'shared/web';

interface BusinessCaseCancellationDialogProps extends TestIdProps {
  businessCaseId: string;
  customerId?: string;
  onCancel?: () => void;
}

type FormValues = {
  reasonCloseCodeId: string;
  reasonCloseNote: string;
};

export function BusinessCaseCancellationDialog(props: BusinessCaseCancellationDialogProps) {
  const navigate = useNavigate();
  const [closeReasons, {isLoading: isCodeListLoading, isError: isCodeListError}] = useCodeList(
    'unsuccessful_business_case_reason'
  );

  const [isDocumentSelected, setIsDocumentSelected] = useState(true);
  const [documentTemplate, setDocumentTemplate] = useState<TemplateBoxTemplate>();
  const [documentNote, setDocumentNote] = useState<string | null>();

  const [createCancellationDocument] = useCreatePurchaseBrokerageCancelationDocumentMutation();
  const [brokerageCancellation] = useEndPurchaseBrokerageBusinessCaseMutation();

  const [getTemplatesByCode, {isLoading, isError}] = useDocumentTemplates();
  const brokerageCancelationTemplates = getTemplatesByCode('purchase-brokerage-cancelation');

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: props.businessCaseId ?? '',
    },
    {skip: isNil(props.businessCaseId)}
  );

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCaseId ?? ''},
    {skip: isNil(props.businessCaseId)}
  );

  const [canReadPurchaseBrokerageCancellationDocument] = usePermissions({
    permissionKeys: ['readPurchaseBrokerageCancellationDocument'],
    scopes: {
      readPurchaseBrokerageCancellationDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const activeDocumentTemplate = documentTemplate ?? brokerageCancelationTemplates.primaryTemplate;

  const reasonOptions =
    closeReasons?.map((reason) => ({
      value: reason.codeId,
      label: reason.name,
    })) ?? [];

  const handleDocumentGenerated = (documents: FileResponseBody) => {
    openDialog(
      <DocumentsDialog
        customerId={props.customerId}
        documents={[documents]}
        isDocxFormat
        recordId={props.businessCaseId}
        resourceId={EntityResourceIds.businessCase}
        permissions={{
          canReadPurchaseBrokerageCancellationDocument,
        }}
        text={i18n.t('page.businessCase.labels.cancellationDocumentCreatedNote')}
      />,
      {
        title: i18n.t('page.businessCase.labels.cancellationDocumentCreated'),
        buttons: [
          {
            variant: 'secondary',
            title: i18n.t('general.actions.close'),
            onClick: closeCurrentDialog,
          },
        ],
      }
    );
  };

  const handleSubmit: FormSubmitHandler<FormValues> = (values) =>
    brokerageCancellation({
      businessCaseId: props.businessCaseId,
      closeBusinessCaseRequestBody: values,
    })
      .unwrap()
      .then(() => {
        navigate(composePath(businessCaseRoutes.overview, {params: {id: props.businessCaseId}}));

        if (!isDocumentSelected || !activeDocumentTemplate) {
          return;
        }

        return createCancellationDocument({
          brokerageContractCancelationRequestBody: {
            businessCaseId: props.businessCaseId,
            templateId: activeDocumentTemplate.id,
            note: documentNote ?? null,
          },
        })
          .unwrap()
          .then(handleDocumentGenerated)
          .catch(handleApiError);
      })
      .then(props.onCancel)
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('page.businessCase.labels.brokerageCancellationDialogText')}
        </Text>
        <DocumentTemplateBox
          title={
            brokerageCancelationTemplates?.definition?.title ||
            i18n.t(`entity.document.labels.purchaseBrokerageCancelation`)
          }
          templates={brokerageCancelationTemplates.defaultTemplates}
          authorizationTemplates={brokerageCancelationTemplates.selectedTemplates}
          authorizationProfileLabel={brokerageCancelationTemplates.selectedAuthProfileLabel}
          selectedTemplate={activeDocumentTemplate}
          isSelected={isDocumentSelected}
          noteLabel={i18n.t('page.businessCase.labels.cancellationReason')}
          onSelect={setIsDocumentSelected}
          onTemplateChange={setDocumentTemplate}
          onNoteChange={setDocumentNote}
          isWithNote
        />
        <Form<FormValues> onSubmit={handleSubmit} schema={schema}>
          {(control) => (
            <VStack spacing={4}>
              <FormField
                data-testid={suffixTestId('reasonChoice', props)}
                label={i18n.t('general.labels.reason')}
                name="reasonCloseCodeId"
                type="choice"
                options={reasonOptions}
                isDisabled={isCodeListLoading || isCodeListError}
                control={control}
                isRequired
              />
              <FormField
                data-testid={suffixTestId('reasonCloseNote', props)}
                label={i18n.t('general.labels.note')}
                name="reasonCloseNote"
                type="textarea"
                minRows={2}
                control={control}
              />
              <ButtonGroup align="right">
                <Button
                  data-testid={suffixTestId('cancelButton', props)}
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                />
                <FormButton
                  data-testid={suffixTestId('submitButton', props)}
                  title={i18n.t('general.actions.endCase')}
                  type="submit"
                  control={control}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </VStack>
    </DataStatus>
  );
}

const schema = object({
  reasonCloseCodeId: yupString.required(),
  reasonCloseNote: yupString,
});
